import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Dialog,
  DialogTitle,
  DialogPanel,
  Transition,
} from '@headlessui/react';
import {
  BreakType,
  CreateDaySequenceInput,
  Job,
} from '@core/generated/gql-types';
import { minutesToMilliseconds } from '@core/util/datetime.util';
import TimePicker from '@/components/shared/TimePicker';
import SingleDatePicker from '@/components/shared/SingleDatePicker';
import {
  formatLoggedTime,
  formatTime,
  getTimestampFromTime,
  getJobStartTimeString,
  getJobEndTimeString,
} from '@/utils/utils';
import { jobDaysOvernight } from '@core/util/jobScheduleBounds.util';
import Button from '@/components/Button';

interface CreateTimesheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  handleCreateTimesheet: (input: CreateDaySequenceInput) => Promise<void>;
}

const DEFAULT_CLOCK_IN_TIME = '09:00';
const DEFAULT_CLOCK_OUT_TIME = '17:00';

const CreateTimesheetModal = ({
  isOpen,
  onClose,
  job,
  handleCreateTimesheet,
}: CreateTimesheetModalProps) => {
  const isOvernightShift = useMemo(
    () => jobDaysOvernight(job.days),
    [job.days?.startTime, job.days?.endTime],
  );

  // Default clock-in day: job start (unix ms), else today — mirrors worker confirm screen.
  const defaultClockInDate = useMemo(() => {
    if (job.startDate) {
      const startDateMs = Number(job.startDate);
      if (!Number.isNaN(startDateMs)) {
        return dayjs(startDateMs).startOf('day').toDate();
      }
    }
    return dayjs().startOf('day').toDate();
  }, [job.startDate]);

  const defaultClockOutDate = useMemo(() => {
    if (isOvernightShift) {
      return dayjs(defaultClockInDate).add(1, 'day').startOf('day').toDate();
    }
    return defaultClockInDate;
  }, [defaultClockInDate, isOvernightShift]);

  const defaultClockInTime = useMemo(() => {
    return getJobStartTimeString(job) || DEFAULT_CLOCK_IN_TIME;
  }, [job]);

  const defaultClockOutTime = useMemo(() => {
    return getJobEndTimeString(job) || DEFAULT_CLOCK_OUT_TIME;
  }, [job]);

  const [clockInDate, setClockInDate] = useState<Date>(defaultClockInDate);
  const [clockOutDate, setClockOutDate] = useState<Date>(defaultClockOutDate);
  const [clockInTime, setClockInTime] = useState<string>(defaultClockInTime);
  const [clockOutTime, setClockOutTime] = useState<string>(defaultClockOutTime);
  const [breakTimeMinutes, setBreakTimeMinutes] = useState<string>('0');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Re-seed the form whenever the modal is opened (so it always reflects the
  // current job, and resets when reopened).
  useEffect(() => {
    if (isOpen) {
      setClockInDate(defaultClockInDate);
      setClockOutDate(defaultClockOutDate);
      setClockInTime(defaultClockInTime);
      setClockOutTime(defaultClockOutTime);
      setBreakTimeMinutes('0');
    }
  }, [
    isOpen,
    defaultClockInDate,
    defaultClockOutDate,
    defaultClockInTime,
    defaultClockOutTime,
  ]);

  const jobCalendarBounds = useMemo(() => {
    const startMs = job.startDate ? Number(job.startDate) : NaN;
    const endMs = job.endDate ? Number(job.endDate) : NaN;
    const min = !Number.isNaN(startMs)
      ? dayjs(startMs).startOf('day').toDate()
      : undefined;
    const max = !Number.isNaN(endMs)
      ? dayjs(endMs).startOf('day').toDate()
      : undefined;
    return { min, max };
  }, [job.startDate, job.endDate]);

  const clockOutDateMax = useMemo(() => {
    if (!jobCalendarBounds.max) return undefined;
    return dayjs(jobCalendarBounds.max)
      .add(isOvernightShift ? 1 : 0, 'day')
      .toDate();
  }, [jobCalendarBounds.max, isOvernightShift]);

  const clockInDateISO = useMemo(
    () => dayjs(clockInDate).format('YYYY-MM-DD'),
    [clockInDate],
  );
  const clockOutDateISO = useMemo(
    () => dayjs(clockOutDate).format('YYYY-MM-DD'),
    [clockOutDate],
  );

  const clockInTimestamp = useMemo(
    () => getTimestampFromTime(clockInDateISO, clockInTime),
    [clockInDateISO, clockInTime],
  );
  const clockOutTimestamp = useMemo(
    () => getTimestampFromTime(clockOutDateISO, clockOutTime),
    [clockOutDateISO, clockOutTime],
  );

  const handleClockInDateChange = (nextIn: Date) => {
    setClockInDate(nextIn);
    const inTs = getTimestampFromTime(
      dayjs(nextIn).format('YYYY-MM-DD'),
      clockInTime,
    );
    let nextOut = clockOutDate;
    if (dayjs(nextOut).isBefore(dayjs(nextIn), 'day')) {
      nextOut = nextIn;
    }
    const outTs = getTimestampFromTime(
      dayjs(nextOut).format('YYYY-MM-DD'),
      clockOutTime,
    );
    if (Number(outTs) <= Number(inTs) && isOvernightShift) {
      nextOut = dayjs(nextIn).add(1, 'day').startOf('day').toDate();
    }
    setClockOutDate(nextOut);
  };

  const timesheetDuration = useMemo(
    () =>
      formatLoggedTime(
        clockInTimestamp,
        clockOutTimestamp,
        minutesToMilliseconds(parseInt(breakTimeMinutes, 10)),
      ),
    [clockInTimestamp, clockOutTimestamp, breakTimeMinutes],
  );

  const isInvalidTimeRange = useMemo(
    () => Number(clockOutTimestamp) <= Number(clockInTimestamp),
    [clockInTimestamp, clockOutTimestamp],
  );

  const invalidInput = useMemo(
    () => isInvalidTimeRange || timesheetDuration === '0 hrs',
    [isInvalidTimeRange, timesheetDuration],
  );

  // Triggered when the time range itself is fine but breaks consume the
  // entire shift (e.g. 60min break on a 60min shift).
  const isBreakTooLong = useMemo(
    () => !isInvalidTimeRange && timesheetDuration === '0 hrs',
    [isInvalidTimeRange, timesheetDuration],
  );

  // Match overnight job behaviour: if clock-out on the same local day as clock-in would not
  // be after clock-in, advance clock-out to the next day (same idea as handleClockInDateChange).
  useEffect(() => {
    if (!isOpen || !isOvernightShift) return;
    const inTs = Number(getTimestampFromTime(clockInDateISO, clockInTime));
    const outTs = Number(getTimestampFromTime(clockOutDateISO, clockOutTime));
    if (
      Number.isNaN(inTs) ||
      Number.isNaN(outTs) ||
      outTs > inTs ||
      !dayjs(clockOutDate).isSame(dayjs(clockInDate), 'day')
    ) {
      return;
    }
    setClockOutDate(dayjs(clockInDate).add(1, 'day').startOf('day').toDate());
  }, [
    isOpen,
    isOvernightShift,
    clockInDate,
    clockOutDate,
    clockInDateISO,
    clockOutDateISO,
    clockInTime,
    clockOutTime,
  ]);

  const onSubmit = async () => {
    if (isSubmitting || invalidInput) return;

    const breakMinutes = parseInt(breakTimeMinutes) || 0;
    const breaks =
      breakMinutes > 0
        ? [
            {
              // The new day sequence's id isn't known yet client-side; the
              // backend stores these breaks as actuals on the created sequence.
              daySequenceId: '',
              duration: String(minutesToMilliseconds(breakMinutes)),
              type: BreakType.Unpaid,
            },
          ]
        : [];

    const input: CreateDaySequenceInput = {
      jobId: job.id,
      clockInTimestamp,
      clockOutTimestamp,
      breaks,
    };

    setIsSubmitting(true);
    try {
      await handleCreateTimesheet(input);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-60" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl transform overflow-y-auto rounded-2xl bg-white px-6 py-12 text-left align-middle shadow-xl transition-all">
                <DialogTitle
                  as="h3"
                  className="flex items-center justify-center pb-5 text-lg leading-6 font-medium text-gray-900"
                >
                  <span className="py-2 text-lg font-medium text-gray-900">
                    Create Timesheet
                  </span>
                </DialogTitle>

                <div className="flex flex-col space-y-8">
                  <div className="flex items-center justify-between">
                    <span>Logged time</span>
                    <span className="font-medium text-orange-500">
                      {`${formatTime(clockInTimestamp, job)} - ${formatTime(clockOutTimestamp, job)} (${timesheetDuration})`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <span className="shrink-0">Clock-in date</span>
                        <SingleDatePicker
                          value={clockInDate}
                          onChange={handleClockInDateChange}
                          minDate={jobCalendarBounds.min}
                          maxDate={jobCalendarBounds.max}
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <span>Clock-in time</span>
                        <div className="flex-1">
                          <TimePicker
                            value={clockInTime}
                            onChange={setClockInTime}
                            label=""
                            onBlur={() => {}}
                            incrementType="minute"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <span className="shrink-0">Clock-out date</span>
                        <SingleDatePicker
                          value={clockOutDate}
                          onChange={setClockOutDate}
                          minDate={dayjs(clockInDate).startOf('day').toDate()}
                          maxDate={clockOutDateMax}
                          isInvalid={isInvalidTimeRange}
                        />
                      </div>
                      <div className="flex items-center gap-4">
                        <span>Clock-out time</span>
                        <div
                          className="flex-1"
                          // className="min-w-60"
                        >
                          <TimePicker
                            value={clockOutTime}
                            onChange={setClockOutTime}
                            label=""
                            onBlur={() => {}}
                            isInvalid={isInvalidTimeRange}
                            incrementType="minute"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col items-center justify-between gap-2">
                        <span>Clock-in time</span>
                        <div className="min-w-60">
                          <TimePicker
                            value={clockInTime}
                            onChange={setClockInTime}
                            label=""
                            onBlur={() => { }}
                            incrementType="minute"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col items-center justify-between gap-2">
                        <span>Clock-out time</span>
                        <div className="min-w-60">
                          <TimePicker
                            value={clockOutTime}
                            onChange={setClockOutTime}
                            label=""
                            onBlur={() => { }}
                            isInvalid={isInvalidTimeRange}
                            incrementType="minute"
                          />
                        </div>
                      </div>
                    </div>
                    {isInvalidTimeRange && (
                      <p
                        role="alert"
                        className="text-center text-sm text-red-600"
                      >
                        Clock-out must be after clock-in. For overnight shifts,
                        set clock-out to the next calendar day or adjust times.
                      </p>
                    )}
                  </div> */}

                  <div className="flex flex-col items-center justify-between gap-2">
                    <span>Break time (minutes)</span>
                    <div className="min-w-60">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={breakTimeMinutes}
                        aria-invalid={isBreakTooLong || undefined}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value === '' || value === '-') {
                            setBreakTimeMinutes('');
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0) {
                              setBreakTimeMinutes(value);
                            }
                          }
                        }}
                        onBlur={(event) => {
                          if (
                            event.target.value === '' ||
                            isNaN(parseInt(event.target.value, 10))
                          ) {
                            setBreakTimeMinutes('0');
                          }
                        }}
                        className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none ${
                          isBreakTooLong
                            ? 'border-red-500 ring-1 ring-red-500 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                        }`}
                        placeholder="0"
                      />
                    </div>
                    {isBreakTooLong && (
                      <p
                        role="alert"
                        className="text-center text-sm text-red-600"
                      >
                        Break time must be shorter than the shift.
                      </p>
                    )}
                  </div>

                  <div className="mt-4 flex justify-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={onSubmit}
                      disabled={isSubmitting || invalidInput}
                      isLoading={isSubmitting}
                      className="!w-auto !rounded-md !py-2 !text-sm !font-medium"
                    >
                      {isSubmitting ? 'Creating…' : 'Create timesheet'}
                    </Button>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CreateTimesheetModal;
